import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ApiResponse } from '../interfaces/api.interface';

/**
 * Service for managing user settings
 * Backend endpoint: /api/users/settings
 */
@Injectable({
  providedIn: 'root'
})
export class UserSettingsService {
  private readonly API_URL = environment.apiUrl || 'http://localhost:3333/api';
  private readonly BASE_URL = `${this.API_URL}/users/settings`;

  constructor(private http: HttpClient) {}

  /**
   * Get all settings for current user
   * GET /api/users/settings
   */
  getUserSettings(): Observable<ApiResponse<Record<string, string>>> {
    return this.http.get<ApiResponse<Record<string, string>>>(this.BASE_URL);
  }

  /**
   * Get specific setting by key for current user
   * GET /api/users/settings/:key
   */
  getUserSettingByKey(key: string): Observable<ApiResponse<{ key: string; value: string }>> {
    return this.http.get<ApiResponse<{ key: string; value: string }>>(`${this.BASE_URL}/${key}`);
  }

  /**
   * Create/Update setting for current user
   * POST /api/users/settings
   */
  createUserSetting(key: string, value: string): Observable<ApiResponse<{ key: string; value: string }>> {
    return this.http.post<ApiResponse<{ key: string; value: string }>>(this.BASE_URL, { key, value });
  }

  /**
   * Bulk update settings for current user
   * PUT /api/users/settings/bulk
   */
  updateUserSettingsBulk(settings: Array<{ key: string; value: string }>): Observable<ApiResponse<Record<string, string>>> {
    return this.http.put<ApiResponse<Record<string, string>>>(`${this.BASE_URL}/bulk`, { settings });
  }

  /**
   * Update specific setting by key for current user
   * PUT /api/users/settings/:key
   */
  updateUserSettingByKey(key: string, value: string): Observable<ApiResponse<{ key: string; value: string }>> {
    return this.http.put<ApiResponse<{ key: string; value: string }>>(`${this.BASE_URL}/${key}`, { value });
  }

  /**
   * Delete setting by key for current user
   * DELETE /api/users/settings/:key
   */
  deleteUserSetting(key: string): Observable<ApiResponse<void>> {
    return this.http.delete<ApiResponse<void>>(`${this.BASE_URL}/${key}`);
  }

  // ===== CHAT SETTINGS METHODS =====

  /**
   * Get chat settings (notifications + privacy)
   */
  getChatSettings(): Observable<any> {
    return this.http.get<any>(`${this.BASE_URL}/chat`);
  }

  /**
   * Update chat notification settings
   */
  updateChatNotificationSettings(settings: any): Observable<any> {
    return this.http.put<any>(`${this.BASE_URL}/chat/notifications`, settings);
  }

  /**
   * Update chat privacy settings
   */
  updateChatPrivacySettings(settings: any): Observable<any> {
    return this.http.put<any>(`${this.BASE_URL}/chat/privacy`, settings);
  }
}
